import {Colors} from '../../../common/const';
import {groupBy, hexToRgba} from 'app/common/utils';
import {DataFilterExtension} from '@deck.gl/extensions';
import {GeoJsonLayer, IconLayer} from '@deck.gl/layers';
import {TrafficsLightPopupComponent} from '../traffics-light-popup/traffics-light-popup.component';
import * as moment from 'moment';
import {BaseLayerGeoJSONDeck} from '../../../common/layers/baseLayerGeoJSONDeck';
import {LAYERS_ID} from './constants';
import {ALL_WAZE_SUBTYPES} from '../../../common/waze-subtypes';
import {RealTimePopupComponent} from '../../../real-time/real-time/real-time-popup/real-time-popup.component';

export class RoutingLayer extends BaseLayerGeoJSONDeck {
    poisLayerSourceVisible = [];
    poisLayerTypeVisible = [];
    icon: string;
    layers = {
        [LAYERS_ID.ROUTING_INFORMO_LINE_LAYER]: (
            new GeoJsonLayer({
                id: LAYERS_ID.ROUTING_INFORMO_LINE_LAYER,
                getFillColor: [140, 207, 215, 100],
                opacity: 0.5,
                getLineColor: f => this.getLineLayerColor(f.properties),
                getLineWidth: _ => 3,
                lineWidthMinPixels: 3,
            })
        ),
        [LAYERS_ID.ROUTING_INFORMO_POIS_LAYER]: (
            new IconLayer({
                id: LAYERS_ID.ROUTING_INFORMO_POIS_LAYER,
                onClick: (e) => this._clickOnLayer(e),
                onHover: ({object}) => this.hover(object !== undefined),
                pickable: true,
                overLayerId: LAYERS_ID.ROUTING_INFORMO_LINE_LAYER,
                getPosition: d => d.geometry.coordinates,
                getSize: f => this.getPoisLayerWidth(f.properties),
                getIcon: f => {
                    const size = this.getPoisLayerWidth(f.properties);
                    return {
                        url: this.getPoisLayerIcon(f.properties),
                        height: size,
                        width: size
                    };
                },
                getFilterValue: d => {
                    return [
                        Number(this.poisLayerSourceVisible.includes(d.properties.source)),
                        Number(this.poisLayerTypeVisible.includes(d.properties.layer))
                    ];
                },
                filterRange: [
                    [1, 1],
                    [1, 1]
                ],
                extensions: [new DataFilterExtension({filterSize: 2})],
                updateTriggers: {
                    getFilterValue: [
                        this.poisLayerSourceVisible,
                        this.poisLayerTypeVisible
                    ]
                }
            })
        ),
    };

    constructor(private city, private mode) {
        super(LAYERS_ID.ROUTING_INFORMO_LAYER);
    }


    getLineLayerColor(properties) {
        const categories = ['FLUID', 'SLOW', 'HOLD', 'CONGESTION', 'CLOSED'];
        const colors = [
            Colors.fluid,
            Colors.level2,
            Colors.level3,
            Colors.level5,
            Colors.closed,
        ];
        const color = colors[categories.indexOf(properties.status)];
        return hexToRgba(color || '#000');
    }

    getPoisLayerWidth(properties) {
        const zoom = this.mapContext.mapInstance.getZoom();
        if (properties.type === 'ACOUSTIC_WARNINGS') {
            return zoom >= 10 && zoom <= 14 ? 18 : 10;
            // blend(10, 18, linear(zoom(), 10, 14)),

        } else if (properties.type === 'TRAFFIC_LIGHT') {
            return zoom >= 10 && zoom <= 14 ? 24 : 16;
            // blend(16, 24, linear(zoom(), 10, 14))
        } else {
            return zoom >= 10 && zoom <= 14 ? 30 : 24;
            // blend(24, 30, linear(zoom(), 10, 14)),
        }
    }

    getPoisLayerIcon(properties) {
        const types = ALL_WAZE_SUBTYPES
            .map(wb => wb.name)
            .concat([
                'CAMERA',
                'ACOUSTIC_WARNINGS',
                'TRAFFIC_LIGHT',
                'CONSTRUCTION',
                'ACCIDENT',
                'WEATHERHAZARD',
                'EVENT'
            ]);
        const images = ALL_WAZE_SUBTYPES
            .map(wb => wb.value)
            .concat([
                '/assets/cartografia/camera.png',
                '/assets/cartografia/circle-acoustic.png',
                '/assets/cartografia/traffic-light.png',
                '/assets/cartografia/construction.png',
                '/assets/cartografia/accident.png',
                '/assets/cartografia/hazard.png',
                '/assets/cartografia/event.png'
            ]);
        const idx = types.indexOf(properties.type);
        let image = images[idx];
        if (!image) {
            image = '/assets/cartografia/marker-goal.png';
        }
        return image;
    }

    updateRoute(route) {
        this.setLayerFilters();
        if (!route) {
            this.updateAll({data: this.getEmptySource()});
        } else {
            this.update(LAYERS_ID.ROUTING_INFORMO_POIS_LAYER, {data: route.pois});
            this.update(LAYERS_ID.ROUTING_INFORMO_LINE_LAYER, {data: route.route});
        }
    }

    setLayerFilters() {
        const city = this.city
            .find(c => c.mode === this.mode.mode);

        this.poisLayerTypeVisible = city.types
            .filter(type => type.showing)
            .map(type => type.idText);

        this.poisLayerSourceVisible = city.sources
            .filter(src => src.showing)
            .map(src => src.id);
    }

    _clickOnLayer(event) {
        if (event.object) {
            const featureClicked = event.object.properties;
            const typeName = featureClicked.type;
            let popup = RealTimePopupComponent;
            let properties: any = {
                type: typeName,
                street: !featureClicked.street || featureClicked.street === 'NULL' ?
                    featureClicked.description : featureClicked.street,

            };

            if (typeName === 'CONSTRUCTION' || typeName === 'ACCIDENT' || typeName === 'WEATHERHAZARD') {
                properties = {
                    ...properties,
                    start: featureClicked.time_start ? moment(featureClicked.time_start).unix() * 1000 : null,
                    finish: featureClicked.time_finish ? moment(featureClicked.time_finish).unix() * 1000 : null,
                };
                if (typeName === 'ACCIDENT' || typeName === 'WEATHERHAZARD') {
                    properties = {
                        ...properties,
                        subtype: featureClicked.subtype,
                        reportdescription: featureClicked.description,
                    };
                }
            } else if (typeName === 'JAM') {
                properties = {
                    ...properties,
                    speed: featureClicked.speed,
                    delay: featureClicked.delay,
                    length: featureClicked.roadlength,
                    level: featureClicked.level,
                };
            } else if (typeName === 'TRAFFIC_LIGHT' || typeName === 'CAMERA') {
                popup = TrafficsLightPopupComponent;
                properties = {
                    ...properties,
                    status: (typeName === 'CAMERA') ? undefined : featureClicked.active,
                    camera: featureClicked.camera,
                };
            } else {
                return;
            }
            this.openPopup(properties, popup, {lng: event.coordinate[0], lat: event.coordinate[1]});
        }
    }

    updateCounter() {
        if (this.visible) {
            this.readViewPortFeatures();
        }
    }

    protected readViewPortFeatures() {
        const grouped = groupBy(
            this.getViewportFeatures(LAYERS_ID.ROUTING_INFORMO_POIS_LAYER),
            'type'
        );

        if (Object.keys(grouped).length) {
            for (const group of Object.keys(grouped)) {
                const mode = 'route';
                const groupType = this.city.find(c => c.mode === mode).types.find(type => group.includes(type.idText));
                if (groupType) {
                    groupType.data = grouped[group];
                }
            }
        } else {
            this.city.find(c => c.mode === 'route').types.forEach(type => type.data = []);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/cities/informo/layers/routingLayer.ts